/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { Checkbox, Col, Row } from 'antd';
import './styles.module.css';
import { TextField, CheckboxField, CampaignField, SelectField } from '@molecules';
import MediaField from '@organisms/MediaField';
import { Block, ErrorMessage, Typography } from '@atoms';

import RelatedContentField from '../RelatedContentField';
import Form from '../Form';

import { FORM_FIELD, FORM_FIELDS, DEFAULT_VALUES } from './constants';

const VideoForm = ({ initialValues = {}, error, form, countries }) => {
  const [isGated, setIsGated] = useState(initialValues.isGatedContent || false);

  const [supportedEbuPlayerCountries, setSupportedEbuPlayerCountries] = useState([]);
  const [supportedVimeoPlayerCountries, setSupportedVimeoPlayerCountries] = useState([]);

  useEffect(() => {
    setSupportedVimeoPlayerCountries(
      form.getFieldValue(FORM_FIELD.VIMEO_PLAYER_SUPPORTED_COUNTRIES.name) ||
        DEFAULT_VALUES[FORM_FIELD.VIMEO_PLAYER_SUPPORTED_COUNTRIES.name],
    );
    setSupportedEbuPlayerCountries(
      form.getFieldValue(FORM_FIELD.EBU_PLAYER_SUPPORTED_COUNTRIES.name) ||
        DEFAULT_VALUES[FORM_FIELD.EBU_PLAYER_SUPPORTED_COUNTRIES.name],
    );
  }, []);

  const handleValuesChange = React.useCallback((change) => {
    if (FORM_FIELD.GATED_CONTENT.name in change) {
      setIsGated(change[FORM_FIELD.GATED_CONTENT.name]);
    }
  }, []);

  const changeTitle = React.useCallback(
    (title) => {
      // for videos without title
      const thumbnailImage = form.getFieldValue(FORM_FIELD.THUMBNAIL_FEATURE_IMAGE.name) || {};
      form.setFieldsValue({
        [FORM_FIELD.THUMBNAIL_FEATURE_IMAGE.name]: { ...thumbnailImage, title },
      });
    },
    [form],
  );

  const handleEbuPlayerCountryChanged = React.useCallback(
    (event) => {
      const { value, checked } = event.target;

      let selectedCountriesForPlayer = [];
      if (form.getFieldValue(FORM_FIELD.EBU_PLAYER_SUPPORTED_COUNTRIES.name)) {
        selectedCountriesForPlayer = [...form.getFieldValue(FORM_FIELD.EBU_PLAYER_SUPPORTED_COUNTRIES.name)];
      }

      if (checked) {
        const country = countries.data.find((c) => c.id === value);
        selectedCountriesForPlayer = [...selectedCountriesForPlayer, country];
      } else {
        if (selectedCountriesForPlayer.length > 0) {
          selectedCountriesForPlayer = selectedCountriesForPlayer.filter((c) => c.id !== value);
        }
      }

      form.setFieldsValue({
        [FORM_FIELD.EBU_PLAYER_SUPPORTED_COUNTRIES.name]: selectedCountriesForPlayer,
      });

      setSupportedEbuPlayerCountries(selectedCountriesForPlayer);
    },
    [form],
  );

  const handleVimeoPlayerCountryChanged = React.useCallback(
    (event) => {
      const { value, checked } = event.target;

      let selectedCountriesForPlayer = [];

      if (form.getFieldValue(FORM_FIELD.VIMEO_PLAYER_SUPPORTED_COUNTRIES.name)) {
        selectedCountriesForPlayer = [...form.getFieldValue(FORM_FIELD.VIMEO_PLAYER_SUPPORTED_COUNTRIES.name)];
      }

      if (checked) {
        const country = countries.data.find((c) => c.id === value);
        selectedCountriesForPlayer = [...selectedCountriesForPlayer, country];
      } else {
        if (selectedCountriesForPlayer.length > 0) {
          selectedCountriesForPlayer = selectedCountriesForPlayer.filter((c) => c.id !== value);
        }
      }

      form.setFieldsValue({
        [FORM_FIELD.VIMEO_PLAYER_SUPPORTED_COUNTRIES.name]: selectedCountriesForPlayer,
      });

      setSupportedVimeoPlayerCountries(selectedCountriesForPlayer);
    },
    [form],
  );

  const regions = {
    Africa: [
      'GAM',
      'CPV',
      'GEQ',
      'CIV',
      'LBA',
      'ESH',
      'ETH',
      'SSD',
      'MYT',
      'MLI',
      'BEN',
      'COM',
      'LBR',
      'CAF',
      'REU',
      'TOG',
      'TUN',
      'COD',
      'NAM',
      'NIG',
      'CMR',
      'MOZ',
      'CHA',
      'KEN',
      'SOM',
      'SEY',
      'MAD',
      'TAN',
      'STP',
      'SEN',
      'SUD',
      'NGR',
      'DJI',
      'BOT',
      'ALG',
      'GAB',
      'LES',
      'MAR',
      'RWA',
      'MRI',
      'UGA',
      'CGO',
      'RSA',
      'BDI',
      'BUR',
      'ZAM',
      'SLE',
      'MTN',
      'SHN',
      'GBS',
      'GUI',
      'MAW',
      'EGY',
      'ZIM',
      'ANG',
      'GHA',
      'ERI',
      'SWZ',
      'IOT',
    ],
    Europe: [
      'ALA',
      'FIN',
      'CRO',
      'IRL',
      'AUT',
      'UKR',
      'SUI',
      'LIE',
      'GGY',
      'ITA',
      'FRO',
      'GRE',
      'RUS',
      'MON',
      'KOS',
      'MNE',
      'POR',
      'NOR',
      'CZE',
      'ALB',
      'CYP',
      'LTU',
      'HUN',
      'BIH',
      'POL',
      'JEY',
      'NED',
      'SLO',
      'ROU',
      'LAT',
      'IMN',
      'FRA',
      'MDA',
      'EST',
      'BUL',
      'SWE',
      'MLT',
      'BLR',
      'VAT',
      'SVK',
      'MKD',
      'GER',
      'AND',
      'SJM',
      'GBR',
      'ISL',
      'LUX',
      'SMR',
      'DEN',
      'GIB',
      'ESP',
      'BEL',
      'SRB',
    ],
    Asia: [
      'TKM',
      'QAT',
      'GEO',
      'BHR',
      'PLE',
      'KGZ',
      'MDV',
      'BAN',
      'AFG',
      'MYA',
      'SYR',
      'BRU',
      'JPN',
      'TPE',
      'LAO',
      'KSA',
      'HKG',
      'IRQ',
      'MGL',
      'CAM',
      'SRI',
      'TLS',
      'PAK',
      'ISR',
      'BHU',
      'THA',
      'TJK',
      'MAC',
      'IRI',
      'TUR',
      'KOR',
      'IND',
      'YEM',
      'UZB',
      'KAZ',
      'UAE',
      'OMA',
      'NEP',
      'PHI',
      'LBN',
      'JOR',
      'SGP',
      'MAS',
      'VIE',
      'INA',
      'PRK',
      'ARM',
      'KUW',
      'CHN',
      'AZE',
    ],
    Americas: [
      'MRT',
      'MNT', // Montserrat
      'TKS', // turks and caicos
      'ESA',
      'TCA',
      'CAY',
      'HAI',
      'ANT',
      'VEN',
      'CAN',
      'VIN',
      'PAR',
      'CUB',
      'BAH',
      'ISV',
      'ARU',
      'CUW',
      'MEX',
      'SUR',
      'ECU',
      'DMA',
      'BLM',
      'IVB',
      'FLK',
      'NCA',
      'MTQ',
      'SKN',
      'BOL',
      'GUA',
      'MAF',
      'HON',
      'MSR',
      'CRC',
      'GRL',
      'USA',
      'DOM',
      'SPM',
      'BRA',
      'BER',
      'SXM',
      'COL',
      'PUR',
      'BIZ',
      'UMI',
      'PER',
      'URU',
      'BAR',
      'GUF',
      'GUY',
      'AIA',
      'GLP',
      'JAM',
      'GRN',
      'CHI',
      'TTO',
      'ARG',
      'LCA',
      'BES',
      'PAN',
      'GDL',
    ],
    Oceania: [
      'MNP',
      'ASA',
      'TUV',
      'SAM',
      'SOL',
      'PCN',
      'NZL',
      'NIU',
      'WLF',
      'PYF',
      'MHL',
      'CXR',
      'FSM',
      'KIR',
      'PNG',
      'VAN',
      'PLW',
      'NCL',
      'CCK',
      'NRU',
      'TKL',
      'TGA',
      'COK',
      'GUM',
      'NFK',
      'AUS',
      'FIJ',
    ],
  };

  return (
    <Form form={form} layout="vertical" initialValues={initialValues} onValuesChange={handleValuesChange}>
      <ErrorMessage error={error} variant="block" spaced />

      <Block>
        <TextField {...FORM_FIELD.TITLE} extra={null} wrapperCol={{ span: 24 }} />
        <MediaField {...FORM_FIELD.THUMBNAIL_FEATURE_IMAGE} handlerTitle={changeTitle} />

        <Block spaced>
          <Typography.Title level={3}>Vimeo</Typography.Title>
          <TextField {...FORM_FIELD.VIMEO_VIDEO_ID} wrapperCol={{ span: 24 }} />
          <TextField {...FORM_FIELD.VIMEO_HASH} wrapperCol={{ span: 24 }} />
          <Form.Item>
            <hr className="divider" />
            {countries && (
              <div>
                {Object.keys(regions).map((region) => {
                  const regionCountries = regions[region];

                  return (
                    <div className="countryRegion" key={region}>
                      <Row style={{ gap: '5px' }}>
                        <Typography.Title level={4}>{region}</Typography.Title>
                      </Row>

                      <div className="countryCheck">
                        {regionCountries.map((countryCode) => {
                          const country = countries.data.find((country) => country.id === countryCode);
                          if (!country) return null;

                          const isChecked = supportedVimeoPlayerCountries.some((co) => co?.id === countryCode);

                          return (
                            <Checkbox
                              key={countryCode}
                              style={{ marginLeft: '0px' }}
                              checked={isChecked}
                              onChange={handleVimeoPlayerCountryChanged}
                              value={countryCode}
                            >
                              {country.name}
                            </Checkbox>
                          );
                        })}
                      </div>
                      <hr className="divider" />
                    </div>
                  );
                })}
              </div>
            )}
          </Form.Item>
        </Block>
        <Block spaced>
          <Typography.Title level={3}>EBU</Typography.Title>
          <TextField {...FORM_FIELD.EBU_PLAYER_URL} wrapperCol={{ span: 24 }} />

          <Form.Item>
            {countries && (
              <div>
                <hr className="divider" />
                {Object.keys(regions).map((region) => {
                  const regionCountries = regions[region];
                  return (
                    <div className="countryRegion" key={region}>
                      <Row>
                        <Typography.Title level={4}>{region}</Typography.Title>
                      </Row>
                      <div className="countryCheck">
                        {regionCountries.map((countryCode) => {
                          const country = countries.data.find((country) => country.id === countryCode);
                          if (!country) return null;

                          const isChecked = supportedEbuPlayerCountries.some((co) => co?.id === countryCode);

                          return (
                            <Checkbox
                              key={countryCode}
                              style={{ marginLeft: '0px' }}
                              checked={isChecked}
                              onChange={handleEbuPlayerCountryChanged}
                              value={countryCode}
                            >
                              {country.name}
                            </Checkbox>
                          );
                        })}
                      </div>
                      <hr className="divider" />
                    </div>
                  );
                })}
              </div>
            )}
          </Form.Item>
        </Block>

        <TextField {...FORM_FIELD.STANDFIRST} extra={null} wrapperCol={{ span: 24 }} />
        <TextField {...FORM_FIELD.LANGUAGE} style={{ display: 'none' }} />
        <TextField {...FORM_FIELD.METATAGS} />

        <CheckboxField {...FORM_FIELD.GATED_CONTENT} />
        {isGated && <CampaignField {...FORM_FIELD.CAMPAIGN} />}
        <Typography.Paragraph>CTA</Typography.Paragraph>
        <Row gutter={16}>
          <Col span={8}>
            <SelectField {...FORM_FIELD.SIGNED_IN_CTA} />
          </Col>
          <Col span={8}>
            <SelectField {...FORM_FIELD.SIGNED_OUT_CTA} />
          </Col>
        </Row>
      </Block>
      <Block spaced>
        <RelatedContentField form={form} fields={FORM_FIELDS} />
      </Block>
    </Form>
  );
};

VideoForm.useForm = () => Form.useForm(FORM_FIELDS, DEFAULT_VALUES);
VideoForm.FORM_FIELD = FORM_FIELD;
VideoForm.DEFAULT_VALUES = DEFAULT_VALUES;

export default VideoForm;
